import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["field", "params", "transport", "subjectGroup", "useCase", "nameInput", "transportLinks", "previewMode", "previewRecordGroup", "previewRecordSelect"]
  static values = { previewRecords: Object }

  connect() {
    this.lastFocusedField = this.fieldTargets[0] || null
    this.nameTouched = false
    this.submitting = false
    this.initialSerializedState = this.serializedState()
    this.fieldTargets.forEach((field) => {
      field.addEventListener("focus", () => {
        this.lastFocusedField = field
      })
    })
    this.element.addEventListener("submit", this.handleSubmit)
    this.updateTransport()
    this.syncTransportLinks()
    this.refreshPreviewRecords()
    this.updatePreviewMode()
    window.addEventListener("beforeunload", this.beforeUnload)
    document.addEventListener("turbo:before-visit", this.handleTurboBeforeVisit)
  }

  disconnect() {
    this.element.removeEventListener("submit", this.handleSubmit)
    window.removeEventListener("beforeunload", this.beforeUnload)
    document.removeEventListener("turbo:before-visit", this.handleTurboBeforeVisit)
  }

  insertParam(event) {
    event.preventDefault()
    const value = `{{ ${event.currentTarget.dataset.value} }}`
    const field = this.lastFocusedField || this.fieldTargets[0]
    if (!field) return

    const start = field.selectionStart ?? field.value.length
    const end = field.selectionEnd ?? field.value.length
    field.value = `${field.value.slice(0, start)}${value}${field.value.slice(end)}`
    field.focus()
    const cursor = start + value.length
    field.setSelectionRange(cursor, cursor)
    field.dispatchEvent(new Event("input", { bubbles: true }))
  }

  refreshParams(event) {
    const useCase = event.target.value
    const container = this.paramsTarget
    if (!container) return

    const variables = JSON.parse(container.dataset.variables || "{}")[useCase] || []
    container.innerHTML = variables.map((variable) => (
      `<button type="button" class="list-group-item list-group-item-action d-flex justify-content-between align-items-center" data-action="template-editor#insertParam" data-value="${variable}"><span>${variable}</span><code class="small">{{ ... }}</code></button>`
    )).join("")

    this.refreshPreviewRecords()
  }

  updateTransport() {
    if (!this.hasTransportTarget || !this.hasSubjectGroupTarget) return

    const emailSelected = this.transportTarget.value === "email"
    this.subjectGroupTarget.hidden = !emailSelected
    if (!emailSelected) {
      const subjectInput = this.subjectGroupTarget.querySelector("input")
      if (subjectInput) subjectInput.value = ""
    }
  }

  markNameTouched() {
    this.nameTouched = true
  }

  suggestName() {
    if (!this.hasNameInputTarget || !this.hasUseCaseTarget || !this.hasTransportTarget) return
    if (this.nameTouched && this.nameInputTarget.value.trim().length > 0) return

    const useCaseLabel = this.useCaseTarget.options[this.useCaseTarget.selectedIndex]?.text || ""
    const transportLabel = this.transportTarget.options[this.transportTarget.selectedIndex]?.text || ""
    if (useCaseLabel && transportLabel) {
      this.nameInputTarget.value = `${useCaseLabel} ${transportLabel}`
    }
  }

  syncTransportLinks() {
    if (!this.hasTransportLinksTarget || !this.hasTransportTarget) return

    const currentTransport = this.transportTarget.value
    this.transportLinksTarget.querySelectorAll("[data-transport-link]").forEach((element) => {
      element.classList.toggle("d-none", element.dataset.transportLink === currentTransport)
    })
  }

  updatePreviewMode() {
    if (!this.hasPreviewRecordGroupTarget || !this.hasPreviewModeTarget) return

    const hasRecords = this.hasPreviewRecordSelectTarget && this.previewRecordSelectTarget.options.length > 1
    const recordMode = this.previewModeTarget.value === "record"
    this.previewRecordGroupTarget.hidden = !(recordMode && hasRecords)
  }

  refreshPreviewRecords() {
    if (!this.hasPreviewRecordSelectTarget || !this.hasUseCaseTarget) return

    const useCase = this.useCaseTarget.value
    const options = this.previewRecordsValue?.[useCase] || []
    const selectedValue = this.previewRecordSelectTarget.value
    const optionTags = [`<option value="">Latest matching record</option>`]

    options.forEach(([label, value]) => {
      const selected = String(value) === String(selectedValue) ? ' selected="selected"' : ""
      optionTags.push(`<option value="${value}"${selected}>${label}</option>`)
    })

    this.previewRecordSelectTarget.innerHTML = optionTags.join("")
    this.updatePreviewMode()
  }

  beforeUnload = (event) => {
    if (this.submitting) return
    if (!this.hasUnsavedChanges()) return
    event.preventDefault()
    event.returnValue = ""
  }

  handleTurboBeforeVisit = (event) => {
    if (this.submitting) return
    if (!this.hasUnsavedChanges()) return
    if (window.confirm("You have unsaved changes. Leave this page?")) return
    event.preventDefault()
  }

  handleSubmit = () => {
    this.submitting = true
  }

  hasUnsavedChanges() {
    return this.serializedState() !== this.initialSerializedState
  }

  serializedState() {
    return JSON.stringify(Array.from(new FormData(this.element).entries()))
  }
}
